import { Node, Edge } from '@xyflow/react';

export interface ValidationError {
  type: 'error' | 'warning';
  message: string;
  nodeId?: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationError[];
}

function getConfigString(node: Node, key: string): string | undefined {
  const rawData = node.data as Record<string, unknown> | undefined;
  const cfg = rawData?.config as unknown as unknown;
  if (typeof cfg === 'object' && cfg !== null) {
    const value = (cfg as Record<string, unknown>)[key];
    return typeof value === 'string' ? value : undefined;
  }
  return undefined;
}

/**
 * Validates a workflow for execution
 */
export function validateWorkflow(
  nodes: Node[],
  edges: Edge[],
): ValidationResult {
  const errors: ValidationError[] = [];
  const warnings: ValidationError[] = [];

  // Check if workflow is empty
  if (nodes.length === 0) {
    errors.push({
      type: 'error',
      message: 'Workflow is empty. Add at least one node.',
    });
    return { isValid: false, errors, warnings };
  }

  // Check for cycles
  const cycleCheck = detectCycles(nodes, edges);
  if (cycleCheck.hasCycle) {
    errors.push({
      type: 'error',
      message: 'Workflow contains a cycle. Remove circular dependencies.',
      nodeId: cycleCheck.cycleNode,
    });
  }

  // Check for disconnected nodes (skip note, input, output, and upload nodes)
  const disconnected = findDisconnectedNodes(nodes, edges);
  if (disconnected.length > 0) {
    disconnected.forEach((nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      const skipTypes = ['input', 'output', 'note', 'upload', 'group'];
      if (node && !skipTypes.includes(node.type || '')) {
        warnings.push({
          type: 'warning',
          message: `Node "${node?.data?.label || nodeId}" is not connected`,
          nodeId,
        });
      }
    });
  }

  // Validate each node
  nodes.forEach((node) => {
    const nodeErrors = validateNode(node, nodes, edges);
    errors.push(...nodeErrors);
  });

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Validates a single node
 */
function validateNode(
  node: Node,
  nodes: Node[],
  edges: Edge[],
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Check node-specific requirements
  switch (node.type) {
    case 'image-generation':
      if (!getConfigString(node, 'model')) {
        errors.push({
          type: 'error',
          message: `Node "${node.data?.label || node.id}" requires a model selection`,
          nodeId: node.id,
        });
      }
      if (
        !getConfigString(node, 'prompt') &&
        !hasIncomingEdge(node.id, edges)
      ) {
        errors.push({
          type: 'error',
          message: `Node "${node.data?.label || node.id}" requires a prompt or input connection`,
          nodeId: node.id,
        });
      }
      break;

    case 'video-generation':
      if (!getConfigString(node, 'model')) {
        errors.push({
          type: 'error',
          message: `Node "${node.data?.label || node.id}" requires a model selection`,
          nodeId: node.id,
        });
      }
      if (
        !getConfigString(node, 'prompt') &&
        !hasIncomingEdge(node.id, edges)
      ) {
        errors.push({
          type: 'error',
          message: `Node "${node.data?.label || node.id}" requires a prompt or input connection`,
          nodeId: node.id,
        });
      }
      break;

    case 'output':
      if (!hasIncomingEdge(node.id, edges)) {
        errors.push({
          type: 'error',
          message: `Output node "${node.data?.label || node.id}" must be connected to an input`,
          nodeId: node.id,
        });
      }
      break;

    case 'talking-video': {
      const incomingEdges = edges.filter((edge) => edge.target === node.id);
      const hasImage = incomingEdges.some((e) => e.targetHandle === 'image');
      const hasVideo = incomingEdges.some((e) => e.targetHandle === 'video');
      const hasAudio = incomingEdges.some((e) => e.targetHandle === 'audio');
      const hasText = incomingEdges.some((e) => e.targetHandle === 'text');

      if (!hasImage && !hasVideo) {
        errors.push({
          type: 'error',
          message: `Node "${node.data?.label || node.id}" requires an image or video input.`,
          nodeId: node.id,
        });
      }

      if (hasImage && hasVideo) {
        errors.push({
          type: 'error',
          message: `Node "${node.data?.label || node.id}" can only accept either an image or a video input, not both.`,
          nodeId: node.id,
        });
      }

      if (!hasAudio) {
        const hasText_ = hasText || !!getConfigString(node, 'text');
        const voiceID = getConfigString(node, 'voiceID');
        if (!hasText_ || !voiceID) {
          errors.push({
            type: 'error',
            message: `Node "${node.data?.label || node.id}" requires either an audio input, or text plus a selected voice.`,
            nodeId: node.id,
          });
        }
      }
      break;
    }
  }

  return errors;
}

/**
 * Detects cycles in the workflow graph using DFS
 */
function detectCycles(
  nodes: Node[],
  edges: Edge[],
): { hasCycle: boolean; cycleNode?: string } {
  const adjacencyList = buildAdjacencyList(nodes, edges);
  const visited = new Set<string>();
  const recursionStack = new Set<string>();

  for (const node of nodes) {
    if (!visited.has(node.id)) {
      const result = dfs(node.id, adjacencyList, visited, recursionStack);
      if (result.hasCycle) {
        return result;
      }
    }
  }

  return { hasCycle: false };
}

function dfs(
  nodeId: string,
  adjacencyList: Map<string, string[]>,
  visited: Set<string>,
  recursionStack: Set<string>,
): { hasCycle: boolean; cycleNode?: string } {
  visited.add(nodeId);
  recursionStack.add(nodeId);

  const neighbors = adjacencyList.get(nodeId) || [];

  for (const neighbor of neighbors) {
    if (!visited.has(neighbor)) {
      const result = dfs(neighbor, adjacencyList, visited, recursionStack);
      if (result.hasCycle) {
        return result;
      }
    } else if (recursionStack.has(neighbor)) {
      return { hasCycle: true, cycleNode: neighbor };
    }
  }

  recursionStack.delete(nodeId);
  return { hasCycle: false };
}

/**
 * Finds nodes that are not connected to any other nodes
 */
function findDisconnectedNodes(nodes: Node[], edges: Edge[]): string[] {
  const connectedNodes = new Set<string>();

  edges.forEach((edge) => {
    connectedNodes.add(edge.source);
    connectedNodes.add(edge.target);
  });

  return nodes
    .filter((node) => !connectedNodes.has(node.id))
    .map((node) => node.id);
}

/**
 * Builds adjacency list from edges
 */
function buildAdjacencyList(
  nodes: Node[],
  edges: Edge[],
): Map<string, string[]> {
  const adjacencyList = new Map<string, string[]>();

  nodes.forEach((node) => {
    adjacencyList.set(node.id, []);
  });

  edges.forEach((edge) => {
    const neighbors = adjacencyList.get(edge.source) || [];
    neighbors.push(edge.target);
    adjacencyList.set(edge.source, neighbors);
  });

  return adjacencyList;
}

/**
 * Checks if a node has any incoming edges
 */
function hasIncomingEdge(nodeId: string, edges: Edge[]): boolean {
  return edges.some((edge) => edge.target === nodeId);
}

/**
 * Gets execution order using topological sort
 * Returns array of levels, where each level contains nodes that can execute in parallel
 */
export function getExecutionOrder(nodes: Node[], edges: Edge[]): string[][] {
  const adjacencyList = buildAdjacencyList(nodes, edges);
  const inDegree = new Map<string, number>();
  const queue: string[] = [];
  const result: string[][] = [];

  // Initialize in-degree for all nodes
  nodes.forEach((node) => {
    inDegree.set(node.id, 0);
  });

  // Calculate in-degree for each node
  edges.forEach((edge) => {
    const currentDegree = inDegree.get(edge.target) || 0;
    inDegree.set(edge.target, currentDegree + 1);
  });

  // Add all nodes with in-degree 0 to queue
  inDegree.forEach((degree, nodeId) => {
    if (degree === 0) {
      queue.push(nodeId);
    }
  });

  // Process queue level by level
  let totalProcessed = 0;
  while (queue.length > 0) {
    // All nodes currently in queue can execute in parallel (same level)
    const levelSize = queue.length;
    const currentLevel: string[] = [];

    for (let i = 0; i < levelSize; i++) {
      const nodeId = queue.shift()!;
      currentLevel.push(nodeId);
      totalProcessed++;

      const neighbors = adjacencyList.get(nodeId) || [];
      neighbors.forEach((neighbor) => {
        const currentDegree = inDegree.get(neighbor) || 0;
        inDegree.set(neighbor, currentDegree - 1);

        if (inDegree.get(neighbor) === 0) {
          queue.push(neighbor);
        }
      });
    }

    result.push(currentLevel);
  }

  // If we didn't process all nodes, there's a cycle
  if (totalProcessed !== nodes.length) {
    throw new Error(
      'Cannot determine execution order: workflow contains a cycle',
    );
  }

  return result;
}

/**
 * Resolves variables in a string using {{variable}} or @NodeLabel syntax.
 *
 * - @NodeLabel is replaced with the result of that node (from context),
 *   matching against node labels (e.g. "#76 Input") and their aliases
 *   without the numeric prefix (e.g. "Input"). Matching is case-insensitive.
 * - {{variable}} is replaced with values from the provided context map.
 */
export function resolveVariables(
  text: string,
  context: Record<string, any>,
  nodes?: Node[],
): string {
  if (!text) return text;

  let resolved = text;

  // Build label-to-ID mapping (case-insensitive) if nodes provided
  if (nodes && nodes.length > 0) {
    const labelToId = new Map<string, string>();

    const escapeRegExp = (str: string) =>
      str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    nodes.forEach((node) => {
      const rawLabel = ((node.data?.label as string) || '').trim();
      if (!rawLabel) return;

      const mainKey = rawLabel.toLowerCase();
      if (!labelToId.has(mainKey)) {
        labelToId.set(mainKey, node.id);
      }

      // Also support labels without leading "#<number> " prefixes, e.g.:
      // "#76 Input" -> "input"
      const alias = rawLabel.replace(/^#\d+\s+/, '').trim();
      const aliasKey = alias.toLowerCase();
      if (alias && aliasKey !== mainKey && !labelToId.has(aliasKey)) {
        labelToId.set(aliasKey, node.id);
      }
    });

    const labelKeys = Array.from(labelToId.keys());

    if (labelKeys.length > 0) {
      // Sort by length desc so longer labels take precedence
      labelKeys.sort((a, b) => (b.length || 0) - (a.length || 0));

      const alternation = labelKeys.map((k) => escapeRegExp(k)).join('|');

      if (alternation) {
        const mentionRegex = new RegExp(`@(${alternation})`, 'gi');

        resolved = resolved.replace(mentionRegex, (match, label) => {
          const nodeId = labelToId.get(label.toLowerCase());
          if (nodeId && Object.prototype.hasOwnProperty.call(context, nodeId)) {
            return String(context[nodeId]);
          }
          return match;
        });
      }
    }
  }

  // Resolve existing {{variable}} syntax for backward compatibility
  resolved = resolved.replace(/\{\{([^}]+)\}\}/g, (match, variable) => {
    const value = context[variable.trim()];
    return value !== undefined ? String(value) : match;
  });

  return resolved;
}
